import Button from '@mui/material/Button'
import CardHeader from '@mui/material/CardHeader'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid2'
import { pdf } from '@react-pdf/renderer'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { chooseDBTranslation } from 'utils/translations'
import DOCXrender from './DOCXrender'
import PDFrender from './PDFrender'
import { usePreviewData } from './PrintPreviewProvider'
import QuickPreview from './QuickPreview'
import { CountriesData, LanguagesData } from 'api/UtilsAPI'
import { IOrganizationColors } from 'types/layoutInterfaces'
import { saveAs } from 'file-saver'
import { CVData } from 'types/printInterfaces'

interface IPreviewProps {
  /* The list of countries. */
  countries: CountriesData
  /* The list of languages. */
  languages: LanguagesData
  /* The profile image to display. */
  profileImage?: string
  /* The organization logo to display. */
  orgLogo?: string
  /* The colors of the organization. */
  orgColors: IOrganizationColors
  /* Indicates if the data is still loading. */
  loading?: boolean
}

/**
 * Renders a preview of the profile with the given data.
 *
 * @param {Object} profileImage - The profile image to display.
 * @param {Object} orgLogo - The organization logo to display.
 * @param {Object} orgColors - The colors of the organization.
 * @param {boolean} loading - Indicates if the data is still loading.
 * @param {Array} countries - The list of countries.
 * @param {Array} languages - The list of languages.
 * @return {React.ReactElement} The rendered preview.
 * @notExported
 */
const Preview: React.FC<IPreviewProps> = ({ countries, languages, profileImage, orgLogo, orgColors, loading }) => {
  const { t, i18n } = useTranslation()
  const { visibleLayout, visibleData, data } = usePreviewData()
  const theme = useTheme()
  const mobileView = useMediaQuery(theme.breakpoints.down('lg'))

  const filename = `${
    chooseDBTranslation(i18n, data?.about.Organization)?.name ?? 'freelancer'
  }_CV_${data?.about.firstName.trim()}_${data?.about.lastName.trim()}_${new Date(Date.now()).toLocaleDateString()}.pdf`

  const document = useMemo(() => {
    if (visibleData && visibleLayout && countries && languages) {
      return (
        <PDFrender
          data={visibleData}
          layout={visibleLayout}
          countries={countries}
          languages={languages}
          profileImage={profileImage}
          orgLogo={orgLogo}
          orgColors={orgColors}
          showContactInfo={visibleLayout?.showContactInfo}
          hideLastUsed={visibleLayout?.hideLastUsed}
          hideSkillExperience={visibleLayout?.hideSkillExperience}
        />
      )
    }
    return null
  }, [countries, languages, orgColors, orgLogo, profileImage, visibleData, visibleLayout])

  const downloadPDF = async (document: React.JSX.Element | null) => {
    if (document) {
      const blob = await pdf(document).toBlob()

      saveAs(blob, filename)
    }
  }

  if (!visibleLayout || !data) return null

  return (
    <Grid>
      <CardHeader
        title={
          <span>
            {t('print.preview.title')} {loading ? <CircularProgress size={25} /> : null}
          </span>
        }
        action={
          <>
            {document && (
              <Button data-testid="button-print-preview-download-pdf" onClick={() => downloadPDF(document)}>
                {t('print.download')}
              </Button>
            )}
            <DOCXrender
              data={data as CVData}
              layout={visibleLayout}
              countries={countries}
              profileImage={profileImage}
              orgLogo={orgLogo}
              orgColors={orgColors}
              languages={languages}
            />
          </>
        }
      />
      {!mobileView ? (
        <>
          {countries && languages && (
            <QuickPreview
              countries={countries}
              languages={languages}
              profileImage={profileImage}
              orgLogo={orgLogo}
              orgColors={orgColors}
            />
          )}
        </>
      ) : (
        <>
          <Typography>{t('noMobileView')}</Typography>
        </>
      )}
    </Grid>
  )
}

export default Preview
